import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useToast } from '../context/ToastContext';
import AvatarPremium from '../components/AvatarPremium';
import IntroCarousel from '../components/IntroCarousel';
import { Users, Eye, Send } from 'lucide-react';

const SLIDES_AMIGOS = [
  { Icon: Users, titulo: 'Bem-vindo ao Social', descricao: 'Conecte-se com amigos e descubra o que eles estão lendo.' },
  { Icon: Eye,   titulo: 'Veja o acervo deles', descricao: 'Siga alguém para ver os livros disponíveis para empréstimo no perfil deles.' },
  { Icon: Send,  titulo: 'Solicite empréstimos', descricao: 'Encontrou um livro que quer ler? Solicite diretamente pelo perfil do amigo.' },
];

function ConexaoCard({ p, seguindoSet, bloqueadosSet, menuConexaoId, setMenuConexaoId, onSeguir, onPararSeguir, onBloquear, onDesbloquear, navigate }) {
  const isSeguindo = seguindoSet.has(p.id);
  const isBloqueado = bloqueadosSet.has(p.id);
  const menuAberto = menuConexaoId === p.id;

  return (
    <div className="bg-white border border-line rounded-[14px] px-4 py-3 flex items-center gap-3"
      style={{ boxShadow: '0 1px 2px rgba(33,28,43,0.04)' }}>
      <div className="cursor-pointer" onClick={() => p.slug && navigate(`/biblioteca/${p.slug}`)}>
        <AvatarPremium fotoUrl={p.foto_url} nome={p.nome} isPremium={p.plano === 'pago'} size={40} />
      </div>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => p.slug && navigate(`/biblioteca/${p.slug}`)}>
        <p className="text-[14px] font-semibold text-ink-900 truncate m-0">{p.nome || 'Usuário'}</p>
        {p.username && <p className="text-[12px] text-ink-400 m-0">@{p.username}</p>}
      </div>
      <div className="relative flex-shrink-0" onClick={e => e.stopPropagation()}>
        {isBloqueado ? (
          <button onClick={() => setMenuConexaoId(menuAberto ? null : p.id)}
            className="text-[12px] px-3 py-1.5 border border-red-200 text-red-400 rounded-[10px]">
            Bloqueado
          </button>
        ) : isSeguindo ? (
          <button onClick={() => setMenuConexaoId(menuAberto ? null : p.id)}
            className="text-[12px] px-3 py-1.5 border border-line text-ink-500 rounded-[10px] flex items-center gap-1">
            Seguindo <span className="text-ink-300">▾</span>
          </button>
        ) : (
          <button onClick={() => onSeguir(p)}
            className="text-[12px] px-3 py-1.5 rounded-[10px] text-white border-0 cursor-pointer"
            style={{ background: '#5B3CE4' }}>
            Seguir
          </button>
        )}
        {menuAberto && (
          <div className="absolute right-0 top-10 bg-white border border-line rounded-[14px] z-20 min-w-[152px] overflow-hidden"
            style={{ boxShadow: '0 8px 24px rgba(33,28,43,0.12)' }}>
            {isBloqueado ? (
              <button onClick={() => onDesbloquear(p.id)}
                className="w-full text-left px-4 py-3 text-[13px] text-ink-700 bg-transparent border-0 cursor-pointer">
                Desbloquear
              </button>
            ) : (
              <>
                <button onClick={() => onPararSeguir(p.id)}
                  className="w-full text-left px-4 py-3 text-[13px] text-ink-700 bg-transparent border-0 cursor-pointer"
                  style={{ borderBottom: '1px solid #F2EEE9' }}>
                  Parar de seguir
                </button>
                <button onClick={() => onBloquear(p.id)}
                  className="w-full text-left px-4 py-3 text-[13px] text-red-500 bg-transparent border-0 cursor-pointer">
                  Bloquear
                </button>
              </>
            )}
            <button onClick={() => setMenuConexaoId(null)}
              className="w-full text-left px-4 py-3 text-[13px] text-ink-400 bg-transparent border-0 cursor-pointer"
              style={{ borderTop: '1px solid #F2EEE9' }}>
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Amigos({ userId, setTela }) {
  const [mostrarIntro, setMostrarIntro] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [aba, setAba] = useState('seguindo');
  const [seguindoList, setSeguindoList] = useState([]);
  const [seguidoresList, setSeguidoresList] = useState([]);
  const [seguindoSet, setSeguindoSet] = useState(new Set());
  const [bloqueadosSet, setBloqueadosSet] = useState(new Set());
  const [busca, setBusca] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [menuConexaoId, setMenuConexaoId] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [livrosPorUsuario, setLivrosPorUsuario] = useState({});
  const timerBusca = useRef(null);

  useEffect(() => {
    if (userId && !localStorage.getItem(`tour_amigos_${userId}`)) {
      setMostrarIntro(true);
    }
  }, [userId]);

  async function buscarLivrosDasPessoas(userIds) {
    if (!userIds.length) return;
    const { data } = await supabase
      .from('livros')
      .select('user_id, capa_url, titulo')
      .in('user_id', userIds)
      .not('capa_url', 'is', null);
    const mapa = {};
    (data || []).forEach(l => {
      if (!mapa[l.user_id]) mapa[l.user_id] = [];
      if (mapa[l.user_id].length < 3) mapa[l.user_id].push(l);
    });
    setLivrosPorUsuario(prev => ({ ...prev, ...mapa }));
  }

  useEffect(() => {
    if (!userId) return;
    async function carregar() {
      const [{ data: seguindoRows }, { data: seguidoresRows }, { data: bloqueados }] = await Promise.all([
        supabase.from('seguidores').select('seguido_id').eq('seguidor_id', userId),
        supabase.from('seguidores').select('seguidor_id').eq('seguido_id', userId),
        supabase.from('bloqueios').select('bloqueado_id').eq('bloqueador_id', userId),
      ]);
      const seguindoIds = (seguindoRows || []).map(r => r.seguido_id);
      const seguidoresIds = (seguidoresRows || []).map(r => r.seguidor_id);
      const [{ data: seguindoData }, { data: seguidoresData }] = await Promise.all([
        seguindoIds.length > 0
          ? supabase.from('perfis').select('id, nome, username, foto_url, slug, plano').in('id', seguindoIds)
          : Promise.resolve({ data: [] }),
        seguidoresIds.length > 0
          ? supabase.from('perfis').select('id, nome, username, foto_url, slug, plano').in('id', seguidoresIds)
          : Promise.resolve({ data: [] }),
      ]);
      const lista = seguindoData || [];
      setSeguindoList(lista);
      setSeguindoSet(new Set(lista.map(p => p.id)));
      setSeguidoresList(seguidoresData || []);
      setBloqueadosSet(new Set((bloqueados || []).map(r => r.bloqueado_id)));
      setCarregando(false);
      const todasIds = [...new Set([...seguindoIds, ...seguidoresIds])];
      if (todasIds.length > 0) buscarLivrosDasPessoas(todasIds);
    }
    carregar();
  }, [userId]);

  useEffect(() => {
    clearTimeout(timerBusca.current);
    const q = busca.replace(/^@/, '').trim();
    if (q.length < 2) { setResultadosBusca([]); return; }
    setBuscando(true);
    timerBusca.current = setTimeout(async () => {
      const { data } = await supabase
        .from('perfis').select('id, nome, username, foto_url, slug, plano')
        .neq('id', userId)
        .or(`nome.ilike.%${q}%,username.ilike.%${q}%`)
        .limit(15);
      setResultadosBusca(data || []);
      setBuscando(false);
      const ids = (data || []).map(p => p.id);
      if (ids.length) buscarLivrosDasPessoas(ids);
    }, 350);
    return () => clearTimeout(timerBusca.current);
  }, [busca, userId]);

  async function handleSeguir(p) {
    await supabase.from('seguidores').insert({ seguidor_id: userId, seguido_id: p.id });
    setSeguindoSet(prev => new Set([...prev, p.id]));
    setSeguindoList(prev => [...prev, p]);
    showToast(`Seguindo ${p.nome}`, 'success');
  }

  async function handlePararSeguir(alvoId) {
    await supabase.from('seguidores').delete().eq('seguidor_id', userId).eq('seguido_id', alvoId);
    setSeguindoSet(prev => { const s = new Set(prev); s.delete(alvoId); return s; });
    setSeguindoList(prev => prev.filter(p => p.id !== alvoId));
    setMenuConexaoId(null);
  }

  async function handleBloquear(alvoId) {
    await Promise.all([
      supabase.from('seguidores').delete().eq('seguidor_id', userId).eq('seguido_id', alvoId),
      supabase.from('seguidores').delete().eq('seguidor_id', alvoId).eq('seguido_id', userId),
      supabase.from('bloqueios').insert({ bloqueador_id: userId, bloqueado_id: alvoId }),
    ]);
    setSeguindoSet(prev => { const s = new Set(prev); s.delete(alvoId); return s; });
    setSeguindoList(prev => prev.filter(p => p.id !== alvoId));
    setSeguidoresList(prev => prev.filter(p => p.id !== alvoId));
    setBloqueadosSet(prev => new Set([...prev, alvoId]));
    setMenuConexaoId(null);
    showToast('Usuário bloqueado', 'success');
  }

  async function handleDesbloquear(alvoId) {
    await supabase.from('bloqueios').delete().eq('bloqueador_id', userId).eq('bloqueado_id', alvoId);
    setBloqueadosSet(prev => { const s = new Set(prev); s.delete(alvoId); return s; });
    setMenuConexaoId(null);
    showToast('Usuário desbloqueado', 'success');
  }

  const listaAtiva = aba === 'seguindo' ? seguindoList : seguidoresList;
  const mostraBusca = busca.trim().length >= 2;
  const frosted = {
    background: 'rgba(255,255,255,0.82)',
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    borderTop: '1px solid #F2EEE9',
  };

  function fecharIntroAmigos() {
    localStorage.setItem(`tour_amigos_${userId}`, 'done');
    setMostrarIntro(false);
  }

  const CHIP_COLORS = ['#2D3B55','#5B3CE4','#1E5C3A','#C4622A','#1A5C6E','#8B2FC9','#3D6B2E','#9B7B1A'];
  const todasPessoas = [...seguindoList, ...seguidoresList.filter(p => !seguindoSet.has(p.id))];
  const listaBusca = mostraBusca ? resultadosBusca : todasPessoas;

  function PessoaCardDesktop({ p, idx }) {
    const isSeguindo = seguindoSet.has(p.id);
    const livros = livrosPorUsuario[p.id] || [];
    return (
      <div style={{background:'#fff', borderRadius:18, padding:'20px 14px 16px', boxShadow:'0 1px 4px rgba(33,28,43,0.07)', display:'flex', flexDirection:'column', alignItems:'center', gap:0, textAlign:'center', cursor:'pointer'}}
        onClick={() => p.slug && navigate(`/biblioteca/${p.slug}`)}>
        <AvatarPremium fotoUrl={p.foto_url} nome={p.nome} isPremium={p.plano === 'pago'} size={52} />
        <p style={{fontSize:15, fontWeight:700, color:'#211C2B', margin:'10px 0 2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'100%'}}>{p.nome || 'Usuário'}</p>
        {p.username && <p style={{fontSize:12, color:'#9A93A4', margin:'0 0 10px'}}>@{p.username}</p>}
        <button
          onClick={e => { e.stopPropagation(); isSeguindo ? handlePararSeguir(p.id) : handleSeguir(p); }}
          style={{width:'100%', padding:'9px 0', borderRadius:999, fontSize:13, fontWeight:700, border: isSeguindo ? '1.5px solid #E4E0F0' : 'none', cursor:'pointer', marginBottom:12,
            background: isSeguindo ? '#fff' : '#5B3CE4', color: isSeguindo ? '#6B6478' : '#fff'}}>
          {isSeguindo ? 'Seguindo' : 'Seguir'}
        </button>
        <div style={{display:'flex', gap:6, justifyContent:'center'}}>
          {[0,1,2].map(i => {
            const livro = livros[i];
            return (
              <div key={i} style={{width:28, height:42, borderRadius:7, overflow:'hidden', flexShrink:0, background: livro ? '#ccc' : CHIP_COLORS[(idx*3+i)%CHIP_COLORS.length]}}>
                {livro?.capa_url && <img src={livro.capa_url} alt={livro.titulo} style={{width:'100%', height:'100%', objectFit:'cover'}} />}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper font-sans flex flex-col" style={{height:'100dvh'}} onClick={() => menuConexaoId && setMenuConexaoId(null)}>
      {mostrarIntro && <IntroCarousel slides={SLIDES_AMIGOS} onClose={fecharIntroAmigos} />}

      {/* Mobile header */}
      <div className="lg:hidden" style={{ height: 'env(safe-area-inset-top)', minHeight: 20 }} />
      <div className="px-5 pt-3 pb-3 lg:hidden">
        <h1 className="text-[22px] font-extrabold tracking-tight text-ink-900 mb-3">Amigos</h1>
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input type="text" placeholder="Buscar por nome ou @usuário…" value={busca}
            onChange={e => setBusca(e.target.value)}
            className="w-full pl-9 pr-9 py-[10px] text-[13px] font-medium bg-white border border-line rounded-[12px] outline-none text-ink-900" />
          {busca && (
            <button onClick={() => { setBusca(''); setResultadosBusca([]); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-300 bg-transparent border-0 cursor-pointer p-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden lg:flex flex-col flex-1 overflow-y-auto" style={{scrollbarWidth:'none'}}>
        <div style={{padding:'32px 40px 0'}}>
          <p style={{fontSize:11, fontWeight:800, letterSpacing:'.15em', textTransform:'uppercase', color:'#5B3CE4', margin:0, marginBottom:8}}>Amigos</p>
          <h1 style={{fontSize:36, fontWeight:800, letterSpacing:'-.03em', color:'#211C2B', margin:0, marginBottom:6, lineHeight:1.1}}>Pessoas que compartilham livros</h1>
          <p style={{fontSize:15, color:'#9A93A4', fontWeight:500, margin:'0 0 24px'}}>Siga leitores para ver o acervo deles e receber sugestões.</p>
          <div style={{position:'relative', marginBottom:28}}>
            <svg style={{position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', color:'#BDB7C5'}} xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input type="text" placeholder="Buscar pessoas…" value={busca} onChange={e => setBusca(e.target.value)}
              style={{width:'100%', background:'#fff', border:'1px solid #EDE9F5', borderRadius:14, paddingLeft:42, paddingRight:14, paddingTop:12, paddingBottom:12, fontSize:14, outline:'none', color:'#211C2B', boxSizing:'border-box'}} />
            {busca && (
              <button onClick={() => { setBusca(''); setResultadosBusca([]); }} style={{position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', background:'transparent', border:'none', cursor:'pointer', padding:0}}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9A93A4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div style={{padding:'0 40px 40px'}}>
          {carregando ? (
            <p style={{color:'#9A93A4', fontSize:14, textAlign:'center', paddingTop:40}}>Carregando…</p>
          ) : buscando ? (
            <p style={{color:'#9A93A4', fontSize:14, textAlign:'center', paddingTop:40}}>Buscando…</p>
          ) : listaBusca.length === 0 ? (
            <div style={{textAlign:'center', paddingTop:60}}>
              <p style={{fontSize:16, fontWeight:700, color:'#211C2B', marginBottom:6}}>
                {mostraBusca ? 'Nenhum usuário encontrado' : 'Nenhuma conexão ainda'}
              </p>
              <p style={{fontSize:13, color:'#9A93A4'}}>
                {mostraBusca ? 'Tente outro nome.' : 'Use a busca para encontrar leitores.'}
              </p>
            </div>
          ) : (
            <>
              <h2 style={{fontSize:18, fontWeight:800, color:'#211C2B', margin:'0 0 18px'}}>
                {mostraBusca ? `${listaBusca.length} resultado${listaBusca.length !== 1 ? 's' : ''}` : 'Sugestões para você'}
              </h2>
              <div style={{display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:16}}>
                {listaBusca.map((p, idx) => (
                  <PessoaCardDesktop key={p.id} p={p} idx={idx} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile content */}
      <div className="flex-1 overflow-y-auto pb-[84px] lg:hidden px-5" style={{ scrollbarWidth: 'none' }}>
        {mostraBusca ? (
          /* Search results */
          buscando ? (
            <p className="text-[13px] text-ink-400 text-center py-8 animate-pulse">Buscando…</p>
          ) : resultadosBusca.length === 0 ? (
            <p className="text-[13px] text-ink-400 text-center py-8">Nenhum usuário encontrado</p>
          ) : (
            <div className="flex flex-col gap-2">
              {resultadosBusca.map(p => (
                <ConexaoCard key={p.id} p={p} seguindoSet={seguindoSet} bloqueadosSet={bloqueadosSet}
                  menuConexaoId={menuConexaoId} setMenuConexaoId={setMenuConexaoId}
                  onSeguir={handleSeguir} onPararSeguir={handlePararSeguir}
                  onBloquear={handleBloquear} onDesbloquear={handleDesbloquear}
                  navigate={navigate} />
              ))}
            </div>
          )
        ) : (
          <>
            {/* Seguindo / Seguidores toggle */}
            <div className="flex gap-1 mb-4 p-[5px] rounded-[14px]" style={{ background: '#F2EEE9' }}>
              <button onClick={() => setAba('seguindo')}
                className="flex-1 py-[8px] rounded-[10px] text-[13px] font-bold transition-all"
                style={aba === 'seguindo'
                  ? { background: '#fff', color: '#211C2B', boxShadow: '0 1px 4px rgba(33,28,43,0.10)' }
                  : { background: 'transparent', color: '#9A93A4' }}>
                Seguindo ({seguindoList.length})
              </button>
              <button onClick={() => setAba('seguidores')}
                className="flex-1 py-[8px] rounded-[10px] text-[13px] font-bold transition-all"
                style={aba === 'seguidores'
                  ? { background: '#fff', color: '#211C2B', boxShadow: '0 1px 4px rgba(33,28,43,0.10)' }
                  : { background: 'transparent', color: '#9A93A4' }}>
                Seguidores ({seguidoresList.length})
              </button>
            </div>

            {carregando ? (
              <p className="text-[13px] text-ink-400 text-center py-8 animate-pulse">Carregando…</p>
            ) : listaAtiva.length === 0 ? (
              <div className="flex flex-col items-center text-center gap-2 py-12">
                <div className="w-14 h-14 rounded-[18px] flex items-center justify-center mx-auto mb-1"
                  style={{background:'linear-gradient(150deg,#EDE9FF,#D8D0FF)'}}>
                  <Users size={28} strokeWidth={1.4} color="#5B3CE4" />
                </div>
                <p className="text-[15px] font-bold text-ink-900">
                  {aba === 'seguindo' ? 'Você ainda não segue ninguém' : 'Ninguém te segue ainda'}
                </p>
                <p className="text-[13px] text-ink-400 font-medium">
                  {aba === 'seguindo' ? 'Use a busca acima para encontrar leitores.' : 'Compartilhe seu perfil para ganhar seguidores.'}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {listaAtiva.map(p => (
                  <ConexaoCard key={p.id} p={p} seguindoSet={seguindoSet} bloqueadosSet={bloqueadosSet}
                    menuConexaoId={menuConexaoId} setMenuConexaoId={setMenuConexaoId}
                    onSeguir={handleSeguir} onPararSeguir={handlePararSeguir}
                    onBloquear={handleBloquear} onDesbloquear={handleDesbloquear}
                    navigate={navigate} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom nav — mobile only */}
      <div className="fixed bottom-0 left-0 right-0 z-30 flex items-center px-[18px] lg:hidden"
        style={{ paddingTop: 10, paddingBottom: 'calc(10px + env(safe-area-inset-bottom))', ...frosted }}>
        <button onClick={() => setTela('acervo')}
          className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#9A93A4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0" /><path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0" />
            <line x1="3" y1="6" x2="3" y2="19" /><line x1="12" y1="6" x2="12" y2="19" /><line x1="21" y1="6" x2="21" y2="19" />
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Estante</span>
        </button>
        <button onClick={() => setTela('explorar')}
          className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#9A93A4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Explorar</span>
        </button>
        <div className="flex-none mx-1.5">
          <button onClick={() => setTela('cadastrar')}
            className="w-[52px] h-[52px] rounded-[17px] border-0 cursor-pointer text-white flex items-center justify-center"
            style={{ background: 'linear-gradient(150deg,#6E50F0,#3F27B0)', marginTop: -18, boxShadow: '0 8px 18px rgba(91,60,228,0.40)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
        <button className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#5B3CE4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
            <polyline points="16 11 18 13 22 9" />
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Amigos</span>
        </button>
        <button onClick={() => setTela('clubes')}
          className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#9A93A4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Clubes</span>
        </button>
      </div>
    </div>
  );
}
